import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { ScheduledTask } from '../../types';
import './ScheduleList.css';

type StepType = ScheduledTask['task_type'];

interface ScheduleListProps {
  tasks: ScheduledTask[];
  schedulerEnabled: boolean;
  onRunTask: (_task: ScheduledTask) => void;
  onRemovePost: (_task: ScheduledTask) => void;
  onNavigateToPost: (_outlineId: number) => void;
}

const STEP_ORDER: StepType[] = ['CREATE_ITEM', 'CREATE_MEDIA', 'PUBLISH'];
const STEP_LABEL: Record<StepType, string> = {
  CREATE_ITEM: 'Write content',
  CREATE_MEDIA: 'Generate media',
  PUBLISH: 'Publish',
};
const PUBLISHED_PAGE = 8;

interface PostGroup {
  outlineId: number;
  title: string;
  steps: ScheduledTask[];
  /** Index of the first still-actionable step (pending/blocked), or -1. */
  nextIndex: number;
  publishAt: number;
  doneAt: number;
  isPublished: boolean;
  anyTask: ScheduledTask;
}

function formatWhen(ms: number): string {
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function relativeWhen(ms: number, now: number): string {
  const diff = ms - now;
  const past = diff < 0;
  const mins = Math.round(Math.abs(diff) / 60000);
  if (mins < 1) return 'now';

  let amount: string;
  if (mins < 60) amount = `${mins} min`;
  else if (mins < 60 * 24) {
    const hrs = Math.round(mins / 60);
    amount = `${hrs} hr${hrs === 1 ? '' : 's'}`;
  } else {
    const days = Math.round(mins / (60 * 24));
    amount = `${days} day${days === 1 ? '' : 's'}`;
  }
  return past ? `${amount} ago` : `in ${amount}`;
}

function groupByPost(tasks: ScheduledTask[]): PostGroup[] {
  const map = new Map<number, ScheduledTask[]>();
  tasks.forEach((t) => {
    if (t.outline == null) return;
    const arr = map.get(t.outline);
    if (arr) arr.push(t);
    else map.set(t.outline, [t]);
  });

  const groups: PostGroup[] = [];
  Array.from(map.entries()).forEach(([outlineId, group]) => {
    const active = group.filter((t) => t.status !== 'canceled');
    if (active.length === 0) return; // whole post was removed

    const byType = new Map<StepType, ScheduledTask>();
    group.forEach((t) => byType.set(t.task_type, t));

    const steps = STEP_ORDER.map((tp) => byType.get(tp)).filter(
      (s): s is ScheduledTask => Boolean(s) && s!.status !== 'canceled'
    );
    if (steps.length === 0) return;

    const publishTask = byType.get('PUBLISH');
    const isPublished = publishTask?.status === 'done';
    const nextIndex = steps.findIndex(
      (s) => s.status === 'pending' || s.status === 'blocked'
    );

    const publishAt = publishTask
      ? new Date(publishTask.scheduled_for).getTime()
      : Math.max(...steps.map((s) => new Date(s.scheduled_for).getTime()));
    const doneAt =
      publishTask && isPublished
        ? new Date(publishTask.updated_at).getTime()
        : publishAt;

    groups.push({
      outlineId,
      title: group[0].outline_title || 'Untitled post',
      steps,
      nextIndex,
      publishAt,
      doneAt,
      isPublished,
      anyTask: group[0],
    });
  });

  return groups;
}

function ScheduleList({
  tasks,
  schedulerEnabled,
  onRunTask,
  onRemovePost,
  onNavigateToPost,
}: ScheduleListProps) {
  const [showAllPublished, setShowAllPublished] = useState(false);
  const [errorTask, setErrorTask] = useState<ScheduledTask | null>(null);

  const approvalReasons = [
    'outline_not_approved',
    'item_not_approved',
    'needs approval',
    'not approved',
  ];

  const now = Date.now();
  const groups = groupByPost(tasks);

  const upcoming = groups
    .filter((g) => !g.isPublished)
    .sort((a, b) => a.publishAt - b.publishAt);
  const published = groups
    .filter((g) => g.isPublished)
    .sort((a, b) => b.doneAt - a.doneAt);

  const visiblePublished = showAllPublished
    ? published
    : published.slice(0, PUBLISHED_PAGE);

  const renderStep = (step: ScheduledTask, isNext: boolean) => {
    const at = new Date(step.scheduled_for).getTime();
    const isOverdue = at <= now;
    const lowerError = (step.last_error || '').toLowerCase();
    const needsApproval = approvalReasons.some((reason) => lowerError.includes(reason));
    let dot = 'sc-dot--scheduled';
    let right: React.ReactNode;

    if (step.status === 'done') {
      dot = 'sc-dot--done';
      right = <span className="sc-step-status sc-step-status--muted">Done</span>;
    } else if (step.status === 'running') {
      dot = 'sc-dot--running';
      right = <span className="sc-step-status sc-step-status--running">Running…</span>;
    } else if (step.status === 'failed') {
      dot = 'sc-dot--failed';
      right = (
        <button
          className="sc-step-status sc-step-status--failed sc-step-status--btn"
          onClick={() => setErrorTask(step)}
        >
          Failed — see why
        </button>
      );
    } else if (!schedulerEnabled) {
      right = (
        <span className="sc-step-when">
          {formatWhen(at)} <span className="sc-step-paused">· paused</span>
        </span>
      );
    } else if (needsApproval) {
      dot = isNext ? 'sc-dot--next' : 'sc-dot--scheduled';
      right = (
        <button
          className="sc-step-status sc-step-status--btn sc-step-status--muted"
          onClick={() => onNavigateToPost(step.outline as number)}
        >
          Needs approval →
        </button>
      );
    } else if (step.status === 'blocked') {
      dot = isOverdue ? 'sc-dot--failed' : isNext ? 'sc-dot--next' : 'sc-dot--scheduled';
      right = isOverdue ? (
        <button
          className="sc-step-status sc-step-status--failed sc-step-status--btn"
          onClick={() => setErrorTask(step)}
        >
          Failed — see why
        </button>
      ) : (
        <span className="sc-step-when">
          <span className="sc-step-status sc-step-status--muted">Pending</span>
          <button className="sc-run-btn" onClick={() => onRunTask(step)}>
            Retry now
          </button>
        </span>
      );
    } else {
      // pending
      dot = isNext ? 'sc-dot--next' : 'sc-dot--scheduled';
      right = (
        <span className="sc-step-when">
          <span className="sc-step-status sc-step-status--muted">Pending</span>
          {formatWhen(at)}
          <button className="sc-run-btn" onClick={() => onRunTask(step)}>
            Run now
          </button>
        </span>
      );
    }

    return (
      <li key={step.id} className="sc-step">
        <span className={`sc-dot ${dot}`} aria-hidden="true" />
        <span className="sc-step-label">{STEP_LABEL[step.task_type]}</span>
        <span className="sc-step-right">{right}</span>
      </li>
    );
  };

  const renderUpcomingCard = (g: PostGroup) => (
    <div className="sc-card" key={g.outlineId}>
      <div className="sc-card-head">
        <button className="sc-title" onClick={() => onNavigateToPost(g.outlineId)}>
          {g.title}
        </button>
        <div className="sc-publish">
          <span className="sc-publish-label">Publishes</span>
          <span className="sc-publish-date">{formatWhen(g.publishAt)}</span>
          <span className="sc-publish-rel">{relativeWhen(g.publishAt, now)}</span>
        </div>
        <button
          className="sc-remove"
          onClick={() => onRemovePost(g.anyTask)}
          title="Remove this post from the schedule"
          aria-label="Remove this post from the schedule"
        >
          ✕
        </button>
      </div>
      <ol className="sc-steps">
        {g.steps.map((step, i) => renderStep(step, i === g.nextIndex))}
      </ol>
    </div>
  );

  const renderPublishedRow = (g: PostGroup) => (
    <div className="sc-done-row" key={g.outlineId}>
      <span className="sc-dot sc-dot--done" aria-hidden="true" />
      <button className="sc-title sc-title--sm" onClick={() => onNavigateToPost(g.outlineId)}>
        {g.title}
      </button>
      <span className="sc-done-when">
        Published {formatWhen(g.doneAt)}
        <span className="sc-done-rel"> · {relativeWhen(g.doneAt, now)}</span>
      </span>
      <button
        className="sc-remove"
        onClick={() => onRemovePost(g.anyTask)}
        title="Remove this post from the schedule"
        aria-label="Remove this post from the schedule"
      >
        ✕
      </button>
    </div>
  );

  return (
    <div className="sc-wrap">
      <section className="sc-section">
        <div className="sc-section-head">
          <h2 className="sc-section-title">Coming up</h2>
          <span className="sc-section-count">{upcoming.length}</span>
        </div>
        {upcoming.length > 0 ? (
          <div className="sc-cards">{upcoming.map(renderUpcomingCard)}</div>
        ) : (
          <p className="sc-empty">
            Nothing in the pipeline. Give a post a publish date and its steps show up here.
          </p>
        )}
      </section>

      {published.length > 0 && (
        <section className="sc-section">
          <div className="sc-section-head">
            <h2 className="sc-section-title">Published</h2>
            <span className="sc-section-count">{published.length}</span>
          </div>
          <div className="sc-done-list">{visiblePublished.map(renderPublishedRow)}</div>
          {published.length > PUBLISHED_PAGE && (
            <button
              className="sc-show-more"
              onClick={() => setShowAllPublished((v) => !v)}
            >
              {showAllPublished ? 'Show less' : `Show all ${published.length}`}
            </button>
          )}
        </section>
      )}

      {errorTask &&
        createPortal(
          <div className="modal-overlay" onClick={() => setErrorTask(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Step failed</h2>
                <button
                  className="modal-close"
                  onClick={() => setErrorTask(null)}
                  aria-label="Close dialog"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="sc-error-step">
                  {STEP_LABEL[errorTask.task_type]} · {errorTask.outline_title || 'Untitled post'}
                </p>
                <pre className="sc-error-text">{errorTask.last_error}</pre>
                {errorTask.retries > 0 && (
                  <p className="sc-error-retries">
                    Retried {errorTask.retries} / {errorTask.max_retries} times
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setErrorTask(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default ScheduleList;
